import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseAutosaveOptions<TSnapshot> {
  /** Gates the autosave interval (e.g. data loaded / draft seeded). */
  enabled: boolean;
  /**
   * Returns the current snapshot. The hook JSON-stringifies it for the
   * dirty check against the last-saved baseline. Kept in a ref internally,
   * so a new function identity per render does not tear down the interval.
   */
  getSnapshot: () => TSnapshot;
  /**
   * Invoked with the freshly taken snapshot when it differs from the
   * last-saved baseline. Kept in a ref internally.
   */
  save: (snapshot: TSnapshot) => void;
  /** Mutation in-flight flag; suppresses autosave ticks while a save is pending. */
  isSaving: boolean;
  /** Autosave cadence. Defaults to 3000ms. */
  intervalMs?: number;
  /** Minimum time the "saving" status is displayed before flipping to "saved". Defaults to 600ms. */
  minSavingMs?: number;
  /** How long the "saved" status is displayed before returning to "idle". Defaults to 1600ms. */
  savedDisplayMs?: number;
}

export interface UseAutosaveResult<TSnapshot = unknown> {
  status: AutosaveStatus;
  setStatus: (status: AutosaveStatus) => void;
  /** Serialized snapshot of the last successful save (dirty-check baseline). */
  lastSavedSnapshotRef: MutableRefObject<string>;
  /** Serialized snapshot of the save currently in flight. */
  pendingSaveSnapshotRef: MutableRefObject<string>;
  /**
   * Marks a save as started for non-autosave save paths (deploy, preview,
   * immediate save). Sets status to "saving", records the save start time
   * for the minimum-spinner delay, and stores the pending snapshot.
   */
  beginSave: (serializedSnapshot: string) => void;
  /**
   * Persists the current snapshot immediately when it differs from the
   * last-saved baseline — for paths that must ship saved state (preview,
   * export, deploy). Takes the snapshot via the hook's own `getSnapshot`;
   * the async save is passed per call because these paths await the
   * mutation rather than using the interval's fire-and-forget `save`.
   * No-ops when clean.
   */
  flushIfDirty: (saveAsync: (snapshot: TSnapshot) => Promise<unknown>) => Promise<void>;
  /**
   * Call from the mutation's onSuccess. Advances the baseline (to the
   * pending snapshot, or to `baselineSnapshot` when the server reconciled
   * the payload), then runs the saved -> idle status choreography honoring
   * the minimum-spinner delay.
   */
  markSaveSucceeded: (baselineSnapshot?: string) => void;
  /** Call from the mutation's onError. Sets status to "error". */
  markSaveFailed: () => void;
}

export function useAutosave<TSnapshot>({
  enabled,
  getSnapshot,
  save,
  isSaving,
  intervalMs = 3000,
  minSavingMs = 600,
  savedDisplayMs = 1600,
}: UseAutosaveOptions<TSnapshot>): UseAutosaveResult<TSnapshot> {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const lastSavedSnapshotRef = useRef<string>('');
  const pendingSaveSnapshotRef = useRef<string>('');
  const saveStartTimeRef = useRef<number>(0);

  // Latest-value refs so the interval reads fresh state without being
  // re-created on every render (snapshot inputs change on each keystroke).
  const getSnapshotRef = useRef(getSnapshot);
  const saveRef = useRef(save);
  const isSavingRef = useRef(isSaving);
  useEffect(() => {
    getSnapshotRef.current = getSnapshot;
    saveRef.current = save;
    isSavingRef.current = isSaving;
  });

  const beginSave = useCallback((serializedSnapshot: string) => {
    setStatus('saving');
    saveStartTimeRef.current = Date.now();
    pendingSaveSnapshotRef.current = serializedSnapshot;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const autosaveInterval = setInterval(() => {
      const snapshot = getSnapshotRef.current();
      const serializedSnapshot = JSON.stringify(snapshot);

      if (serializedSnapshot !== lastSavedSnapshotRef.current && !isSavingRef.current) {
        beginSave(serializedSnapshot);
        saveRef.current(snapshot);
      }
    }, intervalMs);

    return () => clearInterval(autosaveInterval);
  }, [enabled, intervalMs, beginSave]);

  const markSaveSucceeded = useCallback((baselineSnapshot?: string) => {
    lastSavedSnapshotRef.current = baselineSnapshot ?? pendingSaveSnapshotRef.current;

    const elapsed = Date.now() - saveStartTimeRef.current;
    const delay = Math.max(0, minSavingMs - elapsed);

    setTimeout(() => {
      setStatus('saved');
      setTimeout(() => setStatus('idle'), savedDisplayMs);
    }, delay);
  }, [minSavingMs, savedDisplayMs]);

  const markSaveFailed = useCallback(() => {
    setStatus('error');
  }, []);

  const flushIfDirty = useCallback(async (saveAsync: (snapshot: TSnapshot) => Promise<unknown>) => {
    const snapshot = getSnapshotRef.current();
    const serializedSnapshot = JSON.stringify(snapshot);
    if (serializedSnapshot === lastSavedSnapshotRef.current) return;
    beginSave(serializedSnapshot);
    await saveAsync(snapshot);
  }, [beginSave]);

  return {
    status,
    setStatus,
    lastSavedSnapshotRef,
    pendingSaveSnapshotRef,
    beginSave,
    flushIfDirty,
    markSaveSucceeded,
    markSaveFailed,
  };
}
